import { ReactNode, useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';

export interface Stat {
  icon: ReactNode;
  value: string | number;
  label: string;
  show?: boolean;
}

interface StatsGridProps {
  stats: Stat[] | Record<string, Stat>;
}

/** One stat pill in the grid */
function StatCard({ icon, value, label }: Stat) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * A responsive grid of property stats.
 * Shows 2 columns on mobile (xs) and 4 columns on desktop (md).
 * Supports both array and object of stats.
 */
export default function StatsGrid({ stats }: StatsGridProps) {
  const statsList = useMemo(() => {
    const list = Array.isArray(stats) ? stats : Object.values(stats);
    return list.filter((s) => s.show !== false);
  }, [stats]);

  return (
    <Grid container spacing={1.25} mb={1.5}>
      {statsList.map((s, index) => (
        <Grid key={`${s.label}-${index}`} size={{ xs: 6, md: 3 }}>
          <StatCard {...s} />
        </Grid>
      ))}
    </Grid>
  );
}
